import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { Review, PaginatedResponse, Client, User } from '../../types';
import ReviewList from '../reviews/ReviewList';
import ClientForm from '../clients/ClientForm';
import UserForm from '../users/UserForm';
import ClientsList from '../clients/ClientsList';
import UsersList from '../users/UsersList';
import SettingsList from '../settings/SettingsList';
import AdminSettingsList from '../settings/AdminSettingsList';
import QuestionList from '../questions/QuestionList';
import Footer from './Footer';
import WidgetConfigPage from '../widget/WidgetConfigPage';
import './Dashboard.css';

// Site Dudes Logo
const siteDudesLogo = '/sd_logo_480px_136px.png';

type TabType = 'reviews' | 'clients' | 'users' | 'questions' | 'client-settings' | 'admin-settings' | 'widget';

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, logout } = useAuth();
  const tabParam = searchParams.get('tab') as TabType | null;
  const [activeTab, setActiveTab] = useState<TabType>(tabParam || 'reviews');
  const [reviews, setReviews] = useState<PaginatedResponse<Review> | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showClientForm, setShowClientForm] = useState(false);
  const [showUserForm, setShowUserForm] = useState(false);
  const [reviewFilters, setReviewFilters] = useState<{
    search?: string;
    start_date?: string;
    end_date?: string;
    client_id?: number;
  }>({});

  useEffect(() => {
    fetchReviews();
  }, [currentPage, reviewFilters]);

  useEffect(() => {
    // Sync active tab with URL on mount or when URL changes
    if (tabParam && ['reviews', 'clients', 'users', 'questions', 'client-settings', 'admin-settings', 'widget'].includes(tabParam) && tabParam !== activeTab) {
      setActiveTab(tabParam);
    } else if (!tabParam && activeTab !== 'reviews') {
      setSearchParams({ tab: activeTab });
    }
  }, [tabParam, activeTab, setSearchParams]);

  useEffect(() => {
    if (activeTab === 'clients') {
      fetchClients();
    } else if (activeTab === 'users') {
      fetchUsers();
      fetchClients(); // Also fetch clients for the UserForm
    } else if (activeTab === 'reviews') {
      fetchClients(); // Fetch clients for the company filter dropdown
    }
  }, [activeTab]);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        per_page: '20'
      });

      // Add filters to query params
      if (reviewFilters.search) {
        params.append('search', reviewFilters.search);
      }
      if (reviewFilters.start_date) {
        params.append('start_date', reviewFilters.start_date);
      }
      if (reviewFilters.end_date) {
        params.append('end_date', reviewFilters.end_date);
      }
      if (reviewFilters.client_id) {
        params.append('client_id', reviewFilters.client_id.toString());
      }

      const response = await api.get<PaginatedResponse<Review>>(
        `/reviews?${params.toString()}`
      );
      setReviews(response.data);
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFiltersChange = (filters: {
    search?: string;
    start_date?: string;
    end_date?: string;
    client_id?: number;
  }) => {
    setReviewFilters(filters);
    setCurrentPage(1); // Reset to first page when filters change
  };

  const fetchClients = async () => {
    try {
      setClientsLoading(true);
      const response = await api.get<Client[]>('/clients');
      setClients(response.data);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    } finally {
      setClientsLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      setUsersLoading(true);
      const response = await api.get<User[]>('/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setUsersLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
  };

  const handleClientCreated = () => {
    fetchClients();
    setShowClientForm(false);
  };

  const handleUserCreated = () => {
    fetchUsers();
    fetchClients(); // Refresh clients list in case it's needed for the form
    setShowUserForm(false);
  };

  return (
    <div className="dashboard">
      {/* Top Bar - White Background with Logo and Title */}
      <div className="top-bar">
        <div className="top-bar-content">
          <a href="/dashboard" className="logo-link" title="Home" onClick={(e) => {
            e.preventDefault();
            navigate('/dashboard');
          }}>
            <img src={siteDudesLogo} alt="Site Dudes Logo" className="site-dudes-logo" />
          </a>
          <span className="platform-name">Site Dudes Reviews</span>
        </div>
      </div>

      {/* Navigation Header - Orange Background */}
      <header className="dashboard-header">
        <div className="header-content">
          <div className="dashboard-title-section">
            <h1>Admin Dashboard</h1>
          </div>
          
          <div className="header-actions">
            <span className="user-info">Welcome, {user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : ''}</span>
            <button onClick={handleLogout} className="btn btn-secondary">
              Logout
            </button>
          </div>
        </div>
        <div className="header-nav">
            <button
              className={`header-nav-link ${activeTab === 'reviews' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('reviews');
                setSearchParams({ tab: 'reviews' });
              }}
            >
              Reviews
            </button>
            <button
              className={`header-nav-link ${activeTab === 'clients' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('clients');
                setSearchParams({ tab: 'clients' });
              }}
            >
              Companies
            </button>
            <button
              className={`header-nav-link ${activeTab === 'users' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('users');
                setSearchParams({ tab: 'users' });
              }}
            >
              Users
            </button>
            <button
              className={`header-nav-link ${activeTab === 'questions' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('questions');
                setSearchParams({ tab: 'questions' });
              }}
            >
              Questions
            </button>
            <button
              className={`header-nav-link ${activeTab === 'client-settings' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('client-settings');
                setSearchParams({ tab: 'client-settings' });
              }}
            >
              Company Settings
            </button>
            <button
              className={`header-nav-link ${activeTab === 'admin-settings' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('admin-settings');
                setSearchParams({ tab: 'admin-settings' });
              }}
            >
              Settings
            </button>
            <button
              className={`header-nav-link ${activeTab === 'widget' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('widget');
                setSearchParams({ tab: 'widget' });
              }}
            >
              Widget
            </button>
          </div>
      </header>

      <div className="dashboard-content">

        {activeTab === 'reviews' && (
          <div className="reviews-section">
            <h2>All Reviews</h2>
            {reviews ? (
              <ReviewList
                reviews={reviews}
                currentPage={currentPage}
                onPageChange={setCurrentPage}
                onFiltersChange={handleFiltersChange}
                onReviewDeleted={fetchReviews}
                loading={loading}
                clients={clients}
              />
            ) : (
              <div className="no-data">No reviews found</div>
            )}
          </div>
        )}

        {activeTab === 'clients' && (
          <div className="clients-section">
            <div className="section-header">
              <h2>Companies</h2>
              <button
                onClick={() => setShowClientForm(!showClientForm)}
                className="btn btn-primary"
              >
                {showClientForm ? 'Hide Form' : 'Create New Company'}
              </button>
            </div>
            {showClientForm && (
              <ClientForm onSuccess={handleClientCreated} />
            )}
            <ClientsList clients={clients} loading={clientsLoading} onClientUpdate={fetchClients} />
          </div>
        )}

        {activeTab === 'users' && (
          <div className="users-section">
            <div className="section-header">
              <h2>Users</h2>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <a
                  href="/dashboard/opted-emails"
                  onClick={(e) => { e.preventDefault(); navigate('/dashboard/opted-emails'); }}
                  className="btn btn-secondary-orange"
                  style={{ textDecoration: 'none', color: 'inherit' }}
                >
                  Opted Emails
                </a>
                <a
                  href="/dashboard/opted-phones"
                  onClick={(e) => { e.preventDefault(); navigate('/dashboard/opted-phones'); }}
                  className="btn btn-secondary-orange"
                  style={{ textDecoration: 'none', color: 'inherit' }}
                >
                  Opted Phones
                </a>
                <a
                  href="/dashboard/sent-review-emails"
                  onClick={(e) => { e.preventDefault(); navigate('/dashboard/sent-review-emails'); }}
                  className="btn btn-secondary-orange"
                  style={{ textDecoration: 'none', color: 'inherit' }}
                >
                  Sent Review Emails
                </a>
                <button
                  onClick={() => setShowUserForm(!showUserForm)}
                  className="btn btn-primary"
                >
                  {showUserForm ? 'Hide Form' : 'Create New User'}
                </button>
              </div>
            </div>
            {showUserForm && (
              <UserForm 
                clients={clients} 
                onSuccess={handleUserCreated}
                clientsLoading={clientsLoading}
              />
            )}
            <UsersList users={users} loading={usersLoading} onUserDeleted={fetchUsers} />
          </div>
        )}

        {activeTab === 'questions' && (
          <div className="questions-section">
            <QuestionList />
          </div>
        )}

        {activeTab === 'client-settings' && (
          <div className="settings-section">
            <h2>Company Settings</h2>
            <SettingsList />
          </div>
        )}

        {activeTab === 'admin-settings' && (
          <div className="settings-section">
            <h2>Admin Settings</h2>
            <AdminSettingsList />
          </div>
        )}

        {activeTab === 'widget' && (
          <div className="settings-section">
            <h2>Review Widget</h2>
            <WidgetConfigPage />
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default AdminDashboard;
